import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { message, theme as antdTheme } from 'antd';
import { ConfigProvider } from 'antd';
import Layout from './components/Layout';
import Welcome from './pages/Welcome';
import WelcomeNew from './pages/WelcomeNew';
import About from './pages/About';
import Services from './pages/Services/index'; // Updated import for new modular Services
import PrivacyPolicy from './pages/PrivacyPolicy'; // New import for Privacy Policy
import MainHeader from './components/MainHeader';
import { ThemeProvider, useTheme } from './components/ParticleBackground';
import { AnimatedSignIn } from './components/ui/sign-in';
import { AuthProvider } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import TermsConditions from './pages/TermsConditions';
import AdminLogin from './components/auth/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import BookingFlow from './pages/BookingFlow';
import MyBookings from './pages/MyBookings';
import BookingDetails from './pages/BookingDetails';
import Profile from './pages/Profile';
import Contact from './pages/Contact';

import MobileLayout from './components/MobileLayout';
import useResponsive from './hooks/useResponsive';

const ResponsivePageWrapper = ({ children }) => {
  const { isMobile } = useResponsive();

  if (isMobile) {
    return <MobileLayout>{children}</MobileLayout>;
  }

  return (
    <>
      <MainHeader />
      <div style={{ paddingTop: '64px', minHeight: '100vh' }}>
        <Layout>
          {children}
        </Layout>
      </div>
    </>
  );
};

// Component to handle Google Sign-in redirect results
const GoogleRedirectHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        // Import Firebase auth methods
        const { getRedirectResult } = await import('firebase/auth');
        const { auth } = await import('./services/firebase/config');

        // Check if we have a redirect result
        const result = await getRedirectResult(auth);

        if (result && result.user) {
          // Check for stored intended booking
          const storedBooking = localStorage.getItem('intendedServiceBooking');

          // Only show success message if we don't already have it shown
          const hasShownMessage = sessionStorage.getItem('googleSignInSuccess');

          if (!hasShownMessage) {
            message.success('Signed in with Google successfully!');
            sessionStorage.setItem('googleSignInSuccess', 'true');
          }

          // Navigate based on stored intentions
          if (storedBooking) {
            localStorage.removeItem('intendedServiceBooking');
            try {
              const service = JSON.parse(storedBooking);
              navigate('/booking', {
                state: {
                  service: service,
                  selectedServices: [service]
                }
              });
            } catch (e) {
              console.error('Error parsing stored booking:', e);
              navigate('/');
            }
          } else {
            navigate('/');
          }

          // Clear the success flag after navigation
          setTimeout(() => {
            sessionStorage.removeItem('googleSignInSuccess');
          }, 2000);
        }
      } catch (error) {
        console.error('Error handling redirect result:', error);
        // If there's an error, don't prevent normal navigation
      }
    };

    // Check immediately and also after a short delay
    handleRedirectResult();
    const timer = setTimeout(handleRedirectResult, 1500);

    return () => clearTimeout(timer);
  }, [navigate]);

  return null;
};

const ThemedApp = () => {
  const { isDark } = useTheme();

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#667eea',
          borderRadius: 12,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
        components: {
          Card: {
            borderRadiusLG: 16,
            paddingLG: 24,
            colorBgContainer: isDark ? 'var(--bg-card-dark)' : 'var(--bg-card-light)',
            colorBorderSecondary: isDark ? 'var(--border-dark)' : 'var(--border-light)',
          },
          Button: {
            borderRadius: 8,
            fontWeight: 600,
            controlHeight: 40,
          },
          Modal: {
            borderRadiusLG: 16,
            colorBgContainer: isDark ? 'var(--bg-card-dark)' : 'var(--bg-card-light)',
          },
          Tabs: {
            borderRadius: 12,
          },
          Menu: {
            colorBgContainer: isDark ? 'var(--bg-secondary-dark)' : 'var(--bg-secondary-light)',
          },
          Input: {
            colorBgContainer: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
            colorBorder: isDark ? 'var(--border-dark)' : 'var(--border-light)',
            colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
          }
        },
      }}
    >
      <AuthProvider>
        <AdminAuthProvider>
          <Routes>
            {/* Auth routes */}
            <Route path="/signin" element={<ResponsivePageWrapper><AnimatedSignIn /></ResponsivePageWrapper>} />
            <Route path="/signup" element={<ResponsivePageWrapper><AnimatedSignIn /></ResponsivePageWrapper>} />

            {/* Public routes */}
            <Route path="/" element={<ResponsivePageWrapper><WelcomeNew /></ResponsivePageWrapper>} />
            <Route path="/welcome" element={<ResponsivePageWrapper><Welcome /></ResponsivePageWrapper>} />
            <Route path="/about" element={<ResponsivePageWrapper><About /></ResponsivePageWrapper>} />
            <Route path="/services" element={<ResponsivePageWrapper><Services /></ResponsivePageWrapper>} />
            <Route path="/privacy" element={<ResponsivePageWrapper><PrivacyPolicy /></ResponsivePageWrapper>} />
            <Route path="/terms" element={<ResponsivePageWrapper><TermsConditions /></ResponsivePageWrapper>} />

            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />

            {/* Booking Flow */}
            <Route path="/booking" element={<ResponsivePageWrapper><BookingFlow /></ResponsivePageWrapper>} />
            <Route path="/my-bookings" element={<ResponsivePageWrapper><MyBookings /></ResponsivePageWrapper>} />
            <Route path="/booking-details/:bookingId" element={<ResponsivePageWrapper><BookingDetails /></ResponsivePageWrapper>} />
            <Route path="/contact" element={<ResponsivePageWrapper><Contact /></ResponsivePageWrapper>} />
            <Route path="/profile" element={<ResponsivePageWrapper><Profile /></ResponsivePageWrapper>} />

            {/* HIPAA Compliance route */}
            <Route path="/hipaa" element={
              <ResponsivePageWrapper>
                <div style={{
                  padding: '64px 32px',
                  textAlign: 'center',
                  background: isDark ? 'var(--bg-primary-dark)' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f8fafc 100%)',
                  minHeight: '80vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}>
                  <h2 style={{
                    fontSize: '2.5rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '16px'
                  }}>
                    HIPAA Compliance
                  </h2>
                  <p style={{ color: isDark ? '#a0aec0' : '#6b7280', fontSize: '1.2rem' }}>Coming Soon</p>
                </div>
              </ResponsivePageWrapper>
            } />
          </Routes>
        </AdminAuthProvider>
      </AuthProvider>
    </ConfigProvider>
  );
};

const App = () => {
  return (
    <ThemeProvider defaultTheme="light">
      <Router>
        <GoogleRedirectHandler />
        <ThemedApp />
      </Router>
    </ThemeProvider>
  );
};

export default App;